import { useState, useEffect } from 'react'
import { Order, api } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

interface UseOrdersFilters {
  mrn?: string
  lastName?: string
  firstName?: string
  status?: string
  department?: string
  dateFrom?: string
  dateTo?: string
}

export function useOrders(filters?: UseOrdersFilters) {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const data = await api.getOrders(filters)
      setOrders(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
      toast.error('Failed to load orders')
    } finally {
      setLoading(false)
    }
  }

  const updateOrderStatus = async (orderId: string, status: Order['order_status']) => {
    try {
      // Find the current order to get old status
      const currentOrder = orders.find(o => o.id === orderId)
      const oldStatus = currentOrder?.order_status
      
      await api.updateOrderStatus(orderId, status, user?.id, oldStatus)
      await fetchOrders() // Refresh the orders list
      toast.success(`Order status updated to ${status}`)
    } catch (err) {
      toast.error('Failed to update order status')
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [filters])

  return {
    orders,
    loading,
    error,
    refetch: fetchOrders,
    updateOrderStatus
  }
}

export function useUnapprovedOrders() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchUnapprovedOrders = async () => {
    if (user?.role !== 'physician' && user?.role !== 'admin') {
      setOrders([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const data = await api.getUnapprovedOrders()
      setOrders(data)
    } catch (err) {
      toast.error('Failed to load unapproved orders')
    } finally {
      setLoading(false)
    }
  }

  const approveOrder = async (orderId: string) => {
    try {
      console.log('approveOrder called with orderId:', orderId)
      console.log('Current orders before removal:', orders.length)
      
      // Find the current order to get old status
      const currentOrder = orders.find(o => o.id === orderId)
      const oldStatus = currentOrder?.order_status
      
      // Optimistically remove the order from the list immediately
      setOrders(prevOrders => {
        const newOrders = prevOrders.filter(order => order.id !== orderId)
        console.log('Orders after optimistic removal:', newOrders.length)
        return newOrders
      })
      
      await api.updateOrderStatus(orderId, 'new_order_placed', user?.id, oldStatus)
      toast.success('Order approved successfully')
      
      // Don't refresh - trust the optimistic update
    } catch (err) {
      console.error('Error in approveOrder:', err)
      toast.error('Failed to approve order')
      // Only refresh on error to restore correct state
      await fetchUnapprovedOrders()
    }
  }

  useEffect(() => {
    fetchUnapprovedOrders()
  }, [user])

  return {
    orders,
    loading,
    refetch: fetchUnapprovedOrders,
    approveOrder
  }
}